import React, {useCallback, useEffect, useState} from "react"
import RouterComponent from "./shared/router"
import {useRecoilState, useRecoilValue, useSetRecoilState} from "recoil"
import {
    globalAccountVerificationProfileState,
    globalBrandConfigState,
    globalBrandModuleSettingState,
    globalBrandSettingState,
    globalColorThemeState,
    globalCurrentPathState,
    globalCurrentPreloadStatusState,
    globalInitLoadingState,
    globalLanguageLoadingState,
    globalMessageState,
    globalNotificationState,
    globalPNServerSettingState,
    globalRunAuthState,
    globalUserDetailState,
    globalUserVerifyState,
} from "./shared/_common/global_state"
import getConfigByName from "./config"
import {ConfigProvider, Image, message, notification, theme} from "antd"
import {useVerification} from "./shared/_common/_authorize"
import usePreload from "./shared/_common/_preload"
import LoadingScreen from "./brand/_auth/loadingScreen/view"
import loginPageTheme from "./brand/_auth/login/view/_shared/loginPageTheme"
import TwoFactorChallengeModal from "./shared/_common/twoFactorChallengeModal"
import PasskeyPromptModal from "./shared/_common/passkeyPromptModal"
import {Helmet} from "react-helmet"
import {BRAND_NAME, MODE_PRODUCTION} from "./config/_variable"
import {useLocation} from "react-router-dom"
import {useTranslation} from "react-i18next"
import EmptyData from "./brand/component/emptyData/view"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import quarterOfYear from "dayjs/plugin/quarterOfYear"
import customParseFormat from "dayjs/plugin/customParseFormat"
import {onMessage} from "firebase/messaging"
import {initializeFirebase, messaging} from "./config/firebaseConfig"
import {useGetHeaderPushNotificationLogByUserId} from "./shared/pages/announcement/function"
import {isPushSupported, registerPushToken} from "./shared/_common/pushPermission"
import {isMobile} from "react-device-detect"
import {apiGetUserVerificationProfile} from "./shared/api/restful/verification";
import {VERIFICATION_API} from "./config/microserviceApis";
import {initGlobalLogging} from "./shared/api/restful/logs/logs";

initGlobalLogging();

dayjs.extend(utc)
dayjs.extend(quarterOfYear)
dayjs.extend(customParseFormat)

function App() {
    const {i18n} = useTranslation()
    const location = useLocation()

    // const [dataLoading, setDataLoading] = useState(true)
    const [brandLoading, setBrandLoading] = useState(true)
    const [layoutLoading, setLayoutLoading] = useState(true)

    const languageLoading = useRecoilValue(globalLanguageLoadingState)
    const initLoading = useRecoilValue(globalInitLoadingState)
    const brandSettingState = useRecoilValue(globalBrandSettingState)
    const verifyState = useRecoilValue(globalUserVerifyState)
    const runAuthState = useRecoilValue(globalRunAuthState)
    const notificationState = useRecoilValue(globalNotificationState)
    const messageState = useRecoilValue(globalMessageState)
    const userDetailState = useRecoilValue(globalUserDetailState)
    const brandModuleSetting = useRecoilValue(globalBrandModuleSettingState)
    const PNServerSetting = useRecoilValue(globalPNServerSettingState)
    const setGlobalPathState = useSetRecoilState(globalCurrentPathState)
    const [brandConfigState, setBrandConfigState] = useRecoilState(globalBrandConfigState)
    const [preloadStatus, setPreloadStatus] = useRecoilState(globalCurrentPreloadStatusState)
    const [colorState, setColorState] = useRecoilState(globalColorThemeState)
    const [accountVerificationProfile, setAccountVerificationProfile] = useRecoilState(globalAccountVerificationProfileState)

    const urlSearchParams = new URLSearchParams(location.search)
    const token = urlSearchParams.get("token")
    const type = urlSearchParams.get("type")
    const lang = urlSearchParams.get("lang")
    const affiliate = urlSearchParams.get("affiliate")

    const userIdStorage = localStorage.getItem("userid") || null

    const tokenStorage = localStorage.getItem("a_t") || null
    const currentBrand = brandConfigState?.brandName
    // Brand whose theme files (public/brand/color/<brand>, brand/font/<brand>.css)
    // this brand borrows. Only set where a brand has no artwork of its own — the
    // worthycodes dev environment reuses the pxmarkets theme rather than carrying
    // a duplicate copy of it.
    const themeBrand = brandConfigState?.themeBrandName || currentBrand

    // the login page ignores the client's theme, so the palette drawn over it follows the page
    const paletteColor =
        location.pathname === "/login" ? loginPageTheme(currentBrand) : colorState

    // For mobile view redirection
    useEffect(() => {
        const host = window.location.hostname;
        const isLocalHost = host === "localhost" || host === "127.0.0.1";

        if (isMobile && !isLocalHost) {
            window.open("https://" + window.location.hostname.replace("member", "mobile") + "/" + window.location.hash, "_self")
        }
    }, [isMobile]);

    const {
        getInitialDataWithoutToken,
        // getFirebaseToken,
        // getBrandSetting,
        getDashboardMarketNews,
        // getLanguages,
        // getReferralDataSetting,
        // getDashboardEducation,
        // getDashboardEducationPosting,
        // getDashboardOpportunity,
        // getDashboardNewsletter,
        // getDashboardPromotion,
        // getIconLists,
        // getBrandModuleSetting,
        // getAvailableProduct,
        getUserPreferenceNavigations,
        getLatestLanguageVersion
    } = usePreload()
    const {
        authorizeUser,
        userGhostLogin,
        trackUserActivity,
        startTokenExpirationCheck,
        handleTokenExpiration,
        verifyCryptoAddressFunction,
        checkTabActivityOnOpening
    } = useVerification()
    const [_, __, fetchPushNotificationLog, ___] = useGetHeaderPushNotificationLogByUserId()
    const setMessage = useSetRecoilState(globalNotificationState)

    const [api, contextHolder] = notification.useNotification({
        stack: {threshold: 3},
    })
    const [messageApi, messageContextHolder] = message.useMessage()

    const openNotificationWithIcon = (type, msg, title, key, icon, onClose = null) => {
        api[type]({
            icon: icon,
            message: title,
            description: msg,
            key: key,
            onClose: onClose,
        })
    }

    const openMessage = (type, content, duration, key) => {
        messageApi.open({
            type: type,
            content: content,
            duration: duration || 3,
            key: key,
        })
    }

    const brandConfig = async () => {
        const config = await getConfigByName()
        localStorage.setItem("graphqlURL", config.graphqlURL)
        localStorage.setItem("instrumentsURL", config.instrumentsURL)
        localStorage.setItem("strategyBuilderURL", config.strategyBuilderURL)

        setBrandConfigState(config)
    }

    const layoutCss = async () => {
        await setLayoutLoading(true)
        if (brandConfigState?.defaultLayout) {
            import(`./shared/css/${"layout" + brandConfigState?.defaultLayout}/style.css`)
                .then(() => {
                    console.info("CSS file imported")
                })
                .catch((error) => {
                    console.error("Error importing CSS file:", error)
                })
        }
        setLayoutLoading(false)
    }

    const brandColorCss = async () => {
        const colorStorage = await (localStorage.getItem("color") || brandSettingState?.theme)

        switch (colorStorage) {
            case "dark":
                localStorage.setItem("color", "dark")
                setColorState("dark")
                break
            case "light":
                localStorage.setItem("color", "light")
                setColorState("light")
                break
            // default:
            //     localStorage.setItem("color", "light");
            //     setColorState("light");
        }
    }

    const brandFontCss = () => {
        import(`./brand/font/${themeBrand}.css`)
            .then(() => {
                console.info("CSS file imported")
            })
            .catch((error) => {
                console.error("Error importing CSS file:", error)
            })
    }

    const preloadApp = useCallback(async () => {
        setPreloadStatus(true)
        // setDataLoading(true)
        const graphql = localStorage.getItem("graphqlURL") || null

        if (graphql) {
            // await getBrandSetting();
            await getLatestLanguageVersion()
            await getInitialDataWithoutToken()

            if (token) {
                await userGhostLogin(token)
            } else if (type === "verify-crypto-address") {
                const email = urlSearchParams.get("email")
                const transactionNo = urlSearchParams.get("transaction")
                await verifyCryptoAddressFunction(email, transactionNo)
                await authorizeUser(tokenStorage, userIdStorage)
            } else if (runAuthState === false) {
                await authorizeUser(tokenStorage, userIdStorage)
            }

            if (tokenStorage) {
                // await getLanguages();
                // setDataLoading(false)
                await getDashboardMarketNews()
                // await getReferralDataSetting();
                // await getDashboardEducation();
                // await getDashboardEducationPosting();
                // await getDashboardOpportunity();
                // await getDashboardNewsletter();
                // await getDashboardPromotion();
                // await getAvailableProduct();
            }
        }
    }, [token, brandConfigState, userIdStorage, brandModuleSetting])

    const setupInitialLanguage = async () => {
        let initLang = userDetailState?.language || localStorage.getItem("language") || lang || "en"
        localStorage.setItem("language", initLang)
        i18n.changeLanguage(initLang)
    }

    const preloadBrand = async () => {
        await setupInitialLanguage()
        setBrandLoading(true)
        await brandConfig()
        // await brandColorCss();
        brandFontCss()
        setBrandLoading(false)
    }

    const runVerifyingFunction = async () => {
        if (verifyState === true) {
            await checkTabActivityOnOpening()
            await trackUserActivity(10 * 60 * 10000000, handleTokenExpiration)
            // await trackUserActivity(60 * 60 * 1000, handleTokenExpiration); // Debug
            await startTokenExpirationCheck(60 * 10000000)
        }
    }

    const initializeFirebaseApp = async () => {
        try {
            await initializeFirebase(PNServerSetting?.firebase?.config)
            refreshPushToken()
        } catch (e) {
            console.error(e)
        }
    }

    // Prompting lives in the profile notification section, because Safari only allows it on a user gesture.
    async function refreshPushToken() {
        try {
            if (!isPushSupported()) {
                console.error("Your browser does not support Firebase Messaging or Messaging is not initialized.")
                return
            }

            if (Notification.permission === "denied") return

            // Safari rejects this outside a user gesture and throws, which the catch below swallows;
            // those users enable it from the button in the profile notification section instead
            if (Notification.permission === "default") {
                const granted = await Notification.requestPermission()
                if (granted !== "granted") return
            }

            await registerPushToken(PNServerSetting?.firebase?.vapid)
        } catch (e) {
            console.error("An error occurred while registering the push notification token:", e)
        }
    }

    const preloadAccountVerificationProfile = async () => {
        try {
            const verificationProfile = await apiGetUserVerificationProfile({}, VERIFICATION_API)

            if (verificationProfile?.status === "Success") {
                setAccountVerificationProfile(verificationProfile?.data?.[0])
            }
        } catch (e) {
            console.error(e)
        }
    }

    useEffect(() => {
        if (verifyState) {
            preloadAccountVerificationProfile()
        }
    }, [verifyState]);

    useEffect(() => {
        if (messaging) {
            onMessage(messaging, (payload) => {
                setMessage({
                    status: "open",
                    title: payload?.data?.title,
                    msg: (
                        <>
                            {payload?.data?.body}
                            <Image src={payload?.data?.image} preview={false}/>
                        </>
                    ),
                    icon: <Image src={payload?.data?.icon} preview={false}/>,
                })

                fetchPushNotificationLog()
            })
        }
    }, [messaging])

    useEffect(() => {
        if (Object.keys(PNServerSetting)?.length > 0) {
            initializeFirebaseApp()
        }
    }, [PNServerSetting])

    useEffect(() => {
        setupInitialLanguage()
        getUserPreferenceNavigations(userDetailState);
    }, [userDetailState, lang, accountVerificationProfile])

    useEffect(() => {
        const storedToken = localStorage.getItem("a_t");
        if (!storedToken) {
            const hash = window.location.hash.substring(1);
            const publicPaths = ["/login", "/register", "/forgot-password", "/verify-email", "/auth/callback"];
            const isPublic = !hash || hash === "/" || publicPaths.some(p => hash === p || hash.startsWith(p + "?") || hash.startsWith(p + "/"));
            if (!isPublic) {
                localStorage.setItem("redirectAfterLogin", hash);
            }
        }
    }, [])

    useEffect(() => {
        preloadBrand()
    }, [])

    useEffect(() => {
        brandColorCss()
    }, [brandSettingState, colorState])

    useEffect(() => {
        layoutCss()
    }, [brandConfigState, token, currentBrand, colorState])

    useEffect(() => {
        if (!preloadStatus && brandConfigState) {
            preloadApp()
        }
    }, [token, brandConfigState, userIdStorage, preloadStatus])

    useEffect(() => {
        runVerifyingFunction()
    }, [verifyState])

    useEffect(() => {
        const {status, msg, title, key, icon, onClose} = notificationState
        if (msg && key !== "systemNotification") {
            openNotificationWithIcon(status, msg, title, key, icon, onClose)
        } else if (MODE_PRODUCTION === false) {
            openNotificationWithIcon(status, msg, title, key, icon, onClose)
        }
    }, [notificationState])

    useEffect(() => {
        const {type, content, duration, key} = messageState
        if (content) {
            openMessage(type, content, duration, key)
        }
    }, [messageState])

    useEffect(() => {
        setGlobalPathState(location.pathname)
    }, [location])

    useEffect(() => {
        if (affiliate) localStorage.setItem("affiliate", affiliate)
    }, [affiliate])

    return (
        <ConfigProvider
            theme={colorState === "dark" ? {algorithm: theme.darkAlgorithm} : {}}
            renderEmpty={() => (
                <div className={"my-3"}>
                    <EmptyData/>
                </div>
            )}
        >
            <Helmet>
                <link rel="stylesheet" href={`./brand/color/${themeBrand}/${paletteColor}.css`}/>
                <link rel="icon" href={brandSettingState?.siteFavicon}/>
                <link rel="apple-touch-icon" href={brandSettingState?.siteFavicon}/>
                <title>{brandSettingState?.companyName}</title>
                <meta name="description" content={brandSettingState?.companyName}/>
            </Helmet>
            {contextHolder}
            {messageContextHolder}
            {/*{!dataLoading && !brandLoading && !layoutLoading && !languageLoading && !brandModuleSettingLoading ? (*/}
            {!languageLoading && !brandLoading && !layoutLoading && !initLoading ? (
                <RouterComponent/>
            ) : (
                <LoadingScreen/>
            )}

            <TwoFactorChallengeModal/>
            <PasskeyPromptModal/>
        </ConfigProvider>
    )
}

export default App
